import type { AppUser } from './hooks/useHRData';
import { insertGlobalLog, type LogUser } from './lib/globalLog';

/**
 * NOTE ON INTERNATIONALIZATION (i18n):
 * This file is intentionally framework-agnostic and is consumed by many React
 * components. It does NOT call `useTranslation()` directly.
 *
 * The exported static data objects (ROLES, DEPARTMENTS, TAB_TITLES) hold
 * GERMAN FALLBACK STRINGS that are used as the second argument of `t()` at
 * the call site, e.g. `t("tabs.buchhaltung", TAB_TITLES.buchhaltung)`. The
 * static PATCH_NOTES array holds historical release content and is rendered
 * verbatim in the Patch Notes modal without translation.
 *
 * If you need a fully translated copy, use a translation key + fallback:
 *   <span>{t("roles.admin", ROLES.admin.name)}</span>
 */

// --- GLOBALES LOGGING ---
/**
 * Thin wrapper over the extracted data layer (src/lib/globalLog.ts — AGENTS.md
 * §2: components/utils must not call supabase.from() directly). LogUser moved
 * with the data layer; re-exported for backward-compatible imports.
 */
export type { LogUser } from './lib/globalLog';

export const logGlobalAction = async (user: LogUser | null | undefined, actionType: string, details: string) => {
  const { error } = await insertGlobalLog(user, actionType, details);
  if (error) {
    console.error("Fehler beim Loggen:", error);
    // Note: This is a developer-facing console error. The visible UI
    // surfaces its own translated feedback via showToast().
  }
};

import { showToast, formatDateDDMMYY } from './utils';

/**
 * Reports a non-sensitive error toast to the user while logging the original
 * error to the browser console for diagnostics. Use this instead of
 * `showToast(e.message, "error")` to avoid leaking raw backend / Firestore
 * error strings into the UI (per SECURITY.md §1.10).
 *
 * @param {string} userMessage - Localized, generic message to display (e.g. t("common.operationFailed")).
 * @param {Error|unknown} [error] - The original error to log client-side for diagnostics.
 * @returns {void}
 */
export const showError = (userMessage: string, error?: unknown) => {
  if (error) {
    console.error("[AdaptHub]", userMessage, error);
  }
  showToast(userMessage, "error");
};

export { showToast, formatDateDDMMYY };

// --- ARBEITSSTANDORTE (HR) ---
/**
 * The two fixed work locations an employee can be assigned to.
 *
 * Deliberately NOT derived from ALLOWED_PROPERTIES: work location is an HR
 * attribute (where the person works), independent of which Apaleo properties
 * the tenant's API token grants access to. A new location means editing this
 * list — one place, both consumers follow.
 */
export const WORK_LOCATIONS = ['BERLIN', 'SIEGEN'] as const;

// --- ROLLEN & RECHTE (RBAC) ---
export const ROLES = {
  admin: {
      name: 'Administrator',
      badge: 'hub-badge hub-badge-purple',
      // 'workflows' = No-Code Automation Builder menu (user-facing Wenn→dann rules;
      // edit rights inside remain admin/management, others see read-only status).
      // 'internalresources' = Interne Ressourcen (duplicated room-check instances).
      tabs: ['dashboard', 'reservations', 'availability', 'explorer', 'roomrack', 'analytics', 'traces', 'handover', 'chat', 'hk', 'ht', 'internalresources', 'hr', 'sales', 'logs', 'users', 'fundsachen', 'kassenbuch', 'buchhaltung', 'xrechnung', 'anleitungen', 'inventory', 'groups', 'offers', 'iframe', 'automations', 'workflows']
    },
    management: {
      name: 'Management',
      badge: 'hub-badge hub-badge-blue',
      tabs: ['dashboard', 'reservations', 'availability', 'explorer', 'roomrack', 'analytics', 'traces', 'handover', 'chat', 'hk', 'ht', 'internalresources', 'hr', 'sales', 'fundsachen', 'kassenbuch', 'xrechnung', 'anleitungen', 'inventory', 'groups', 'offers', 'iframe', 'buchhaltung', 'workflows']
    },
    sales: {
      name: 'Sales',
      badge: 'hub-badge hub-badge-pink',
      tabs: ['dashboard', 'reservations', 'availability', 'handover', 'chat', 'explorer', 'roomrack', 'analytics', 'traces', 'hr', 'sales', 'anleitungen', 'groups', 'offers', 'iframe', 'workflows']
    },
    reception: {
      name: 'Rezeption',
      badge: 'hub-badge hub-badge-succ',
      tabs: ['dashboard', 'reservations', 'availability', 'explorer', 'roomrack', 'traces', 'handover', 'chat', 'hk', 'ht', 'hr', 'fundsachen', 'kassenbuch', 'xrechnung', 'anleitungen', 'groups', 'offers', 'workflows']
    },
    rads: {
      name: 'Interne Ressourcen',
      badge: 'hub-badge hub-badge-indigo',
      tabs: ['dashboard', 'reservations', 'availability', 'handover', 'chat', 'explorer', 'roomrack', 'traces', 'hk', 'ht', 'internalresources', 'hr', 'fundsachen', 'kassenbuch', 'anleitungen', 'inventory']
    },
    housekeeping: {
      name: 'Housekeeping',
      badge: 'hub-badge hub-badge-amber',
      tabs: ['dashboard', 'explorer', 'reservations', 'availability', 'handover', 'chat', 'roomrack', 'traces', 'hk', 'hr', 'fundsachen', 'anleitungen']
    },
    haustechnik: {
      name: 'Haustechnik',
      badge: 'hub-badge hub-badge-cyan',
      tabs: ['dashboard', 'explorer', 'reservations', 'availability', 'handover', 'chat', 'roomrack', 'traces', 'hk', 'ht', 'internalresources', 'hr', 'fundsachen', 'anleitungen']
    },
    hausdame: {
      name: 'Hausdame',
      badge: 'hub-badge hub-badge-amber shadow-xs',
      tabs: ['dashboard', 'explorer', 'reservations', 'availability', 'handover', 'chat', 'roomrack', 'traces', 'hk', 'hr', 'fundsachen', 'ht', 'anleitungen', 'inventory']
    },
    ht_leitung: {
      name: 'HT-Leitung',
      badge: 'hub-badge hub-badge-cyan shadow-xs',
      tabs: ['dashboard', 'explorer', 'reservations', 'availability', 'handover', 'chat', 'roomrack', 'traces', 'hk', 'ht', 'hr', 'fundsachen', 'anleitungen', 'inventory']
    },
    pending: {
      name: 'Neu / Basis',
      badge: 'hub-badge hub-badge-muted',
      tabs: ['hr']
    },
    accountant: {
      name: 'Buchhalter',
      badge: 'hub-badge hub-badge-purple shadow-xs',
      tabs: ['dashboard', 'explorer', 'reservations', 'kassenbuch', 'buchhaltung', 'xrechnung', 'hr', 'traces', 'chat', 'inventory']
    }
  };
export const DEPARTMENTS = {
  FO: { id: 'FO', name: 'Rezeption', badge: 'hub-badge hub-badge-succ', icon: 'Briefcase' },
  HK: { id: 'HK', name: 'Housekeeping', badge: 'hub-badge hub-badge-amber', icon: 'Briefcase' },
  HT: { id: 'HT', name: 'Haustechnik', badge: 'hub-badge hub-badge-cyan', icon: 'Briefcase' },
  IR: { id: 'IR', name: 'Interne Ressourcen', badge: 'hub-badge hub-badge-indigo', icon: 'Briefcase' },
  MG: { id: 'MG', name: 'Management', badge: 'hub-badge hub-badge-blue', icon: 'Briefcase' },
  SL: { id: 'SL', name: 'Sales', badge: 'hub-badge hub-badge-pink', icon: 'Briefcase' },
  DX: { id: 'DX', name: 'Direktion', badge: 'hub-badge hub-badge-purple', icon: 'Briefcase' },
  BH: { id: 'BH', name: 'Buchhaltung', badge: 'hub-badge hub-badge-purple shadow-xs', icon: 'Briefcase' }
};

export const TAB_TITLES = {
  dashboard: 'Dashboard (Prototyp)',
  reservations: 'Reservierungen',
  roomrack: 'Zimmerplan',
  availability: 'Verfügbarkeiten',
  analytics: 'Reporting',
  traces: 'Traces',
  handover: 'Schichtübergabe',
  hk: 'Housekeeping & Bleiber',
  ht: 'Haustechnik',
  internalresources: 'Interne Ressourcen',
  fundsachen: 'Fundsachen',
  hr: 'Personalwesen',
  sales: 'Sales & Revenue',
  logs: 'System Logs',
  users: 'Benutzerverwaltung',
  kassenbuch: 'Kassenbuch',
  buchhaltung: 'Buchhaltung',
  xrechnung: 'XRechnung',
  anleitungen: 'Anleitungen',
  inventory: 'Inventar',
  groups: 'Gruppen & Blocks',
  offers: 'Angebote',
  iframe: 'Custom Iframe',
  automations: 'Automatisierungen',
  workflows: 'Workflows'
};

// --- COMPANY CONTRACTS (FIRMENVERTRÄGE) ---
// See plans/company-contracts-module.md. The contracts module lives inside
// the existing `sales` tab (ADR 6) — no new tab id is needed; the ROLES
// definition above already grants `admin`, `management`, and `sales` access
// to `sales`. The constants below are consumed by Phase 2.2+ UI components
// and by the serverless `api/firmenraten-import.js` endpoint.

/**
 * Lifecycle status of a company-contract document. Persisted verbatim as
 * the `status` field on `tenants/{tenantId}/companyContracts/{contractId}`.
 * `expired` is computed dynamically by the UI when `validTo < today`; the
 * stored value is whatever the user last set (see ADR 8).
 */
export const FIRMENVERTRAEGE_STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  EXPIRED: 'expired',
  ARCHIVED: 'archived',
};

/** Display order for status filters and badges. */
export const FIRMENVERTRAEGE_STATUS_ORDER = [
  FIRMENVERTRAEGE_STATUS.DRAFT,
  FIRMENVERTRAEGE_STATUS.ACTIVE,
  FIRMENVERTRAEGE_STATUS.EXPIRED,
  FIRMENVERTRAEGE_STATUS.ARCHIVED,
];

/**
 * LOS (length-of-stay) tier suffixes. Mirrors `LOS_TIER_BY_MIN_LOS` in
 * `scripts/import-firmenraten.mjs` and `TIERS` in `src/utils/firmenraten.js`.
 * Use this for iteration / display; use `deriveTier()` from firmenraten.js
 * for actual range-based tier classification.
 */
export const LOS_TIERS = ['SHORT', 'MED', 'LONG', 'LONGX'];

/** Prefix for auto-generated contract numbers (e.g. "FC-2026-001"). */
export const CONTRACT_NUMBER_PREFIX = 'FC';

/** Max number of company-contracts loaded per Firestore subscription (AGENTS.md §1). */
export const COMPANY_CONTRACTS_FETCH_LIMIT = 50;

/** Allowed roles for write operations on company-contracts documents. */
export const FIRMENVERTRAEGE_WRITE_ROLES = ['admin', 'management', 'sales'];

/** Allowed roles for delete operations on company-contracts documents. */
export const FIRMENVERTRAEGE_DELETE_ROLES = ['admin', 'management'];

export const areGuestsIdentical = (g1: any, g2: any) => {
  if (!g1 && !g2) return true;
  if (!g1 || !g2) return false;

  const norm = (v: any) => (v || '').toString().trim().toLowerCase();

  const fields = ['firstName', 'lastName', 'email', 'phone', 'title', 'gender', 'nationalityCountryCode', 'birthDate'];
  for (const f of fields) {
    if (norm(g1[f]) !== norm(g2[f])) return false;
  }

  const a1 = g1.address || {};
  const a2 = g2.address || {};
  const addressFields = ['addressLine1', 'postalCode', 'city', 'countryCode'];
  for (const f of addressFields) {
    if (norm(a1[f]) !== norm(a2[f])) return false;
  }

  return true;
};

export const calculateProfileScore = (guest: any) => {
  if (!guest) return 0;
  let score = 0;

  const fields = ['firstName', 'lastName', 'email', 'phone', 'title', 'gender', 'nationalityCountryCode', 'birthDate', 'company'];
  fields.forEach(f => {
    if (guest[f] && String(guest[f]).trim() !== '') score += 1;
  });

  if (guest.address) {
    const addrFields = ['addressLine1', 'postalCode', 'city', 'countryCode'];
    addrFields.forEach(f => {
      if (guest.address[f] && String(guest.address[f]).trim() !== '') score += 1;
    });
  }

  return score;
};


export const generateGuestId = (guest: any) => {
  if (!guest) return "unknown";
  if (guest.email) return guest.email.toLowerCase().trim();
  const first = (guest.firstName || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const last = (guest.lastName || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!first && !last) return "unknown";
  return `${first}_${last}`;
};

/**
 * Normalizes a Supabase `guests` / `users` row into the camelCase shape the
 * React CRM consumers expect.
 *
 * The migration (`scripts/db-migration.cjs:migrateGuests`) writes Firestore's
 * top-level `firstName` / `lastName` into dedicated snake_case columns
 * (`first_name`, `last_name`) and dumps the rest of the profile (status,
 * internalNotes, preferredRoom, companyRates, address, …) into the `data`
 * jsonb column. The React code reads camelCase top-level properties, so we
 * re-hydrate both shapes here. Called once at the fetch boundary in
 * RoomRackView, ReservationsView, and OffersView.
 *
 * @param {Object|null} row - Raw Supabase row.
 * @returns {Object|null} Row with `firstName`/`lastName` exposed and `data`
 *   fields spread to the top level. Pass-through for null/undefined.
 */
export const mapGuestRow = (row: any) => {
  if (!row) return row;
  return {
    ...row,
    ...(row.data || {}),
    firstName: row.first_name ?? row.firstName ?? null,
    lastName: row.last_name ?? row.lastName ?? null,
  };
};

/**
 * Normalizes a Supabase `waiting_list` row into the camelCase shape the
 * React waiting-list consumers expect.
 *
 * Unlike the `guests` table, the column names here differ from the JS field
 * names (not just casing): `start_date` -> `arrival`, `end_date` ->
 * `departure`, `room_type` -> `category`. The `property_id` column does not
 * exist on `waiting_list`; anything stored there lives in the `data` jsonb
 * and is surfaced by the `...(row.data || {})` spread.
 *
 * @param {Object|null} row - Raw Supabase row.
 * @returns {Object|null} Row with camelCase aliases. Pass-through for null/undefined.
 */
export const mapWaitingListRow = (row: any) => {
  if (!row) return row;
  const data = row.data || {};
  const mapped = {
    ...row,
    ...data,
  };
  // Only overwrite with the column value when it is actually populated.
  // Otherwise the spread's value from the data jsonb (which holds the
  // original Firestore camelCase fields) is preserved. This matters for
  // migrated rows where the dedicated snake_case columns are null but the
  // original fields survived inside the data jsonb.
  if (row.guest_name != null) mapped.guestName = row.guest_name;
  if (row.guest_email != null) mapped.guestEmail = row.guest_email;
  if (row.start_date != null) mapped.arrival = row.start_date;
  if (row.end_date != null) mapped.departure = row.end_date;
  if (row.room_type != null) mapped.category = row.room_type;
  return mapped;
};

/**
 * Normalizes a Supabase `overbooking_locks` row into the camelCase shape the
 * React OverbookingLocksModal consumers expect.
 *
 * `start_date` and `end_date` are real snake_case columns on the table, while
 * `propertyId` / `status` / `category` / `updatedBy` / etc. live in the
 * `data` jsonb. The spread exposes the jsonb fields at the top level, and
 * the explicit aliases handle the column-name mismatch for the dates.
 *
 * @param {Object|null} row - Raw Supabase row.
 * @returns {Object|null} Row with camelCase aliases. Pass-through for null/undefined.
 */
export const mapOverbookingLockRow = (row: any) => {
  if (!row) return row;
  const data = row.data || {};
  return {
    ...row,
    ...data,
    arrival: row.start_date ?? data.arrival ?? null,
    departure: row.end_date ?? data.departure ?? null,
  };
};

/**
 * Normalizes a Supabase `offers` row into the camelCase shape the
 * React offer consumers (OffersView, OfferDrawer) expect.
 *
 * The `offers` table stores the full offer payload (arrival, departure,
 * guestInfo, price, offerItems, validUntil, etc.) inside a `data` jsonb
 * column. The top-level columns only carry `id`, `tenant_id`, `status`,
 * `guest_email`, `guest_name`, `expires_at`, `created_at`, `updated_at`.
 *
 * This helper spreads the jsonb payload to the top level so existing
 * consumers can keep reading `offer.arrival`, `offer.guestInfo`,
 * `offer.price`, etc. without knowing about the jsonb wrapper.
 *
 * The top-level `status` column wins over `data.status` because the
 * server-side `handleListOffers` keeps them in sync and may have just
 * flipped a row to `expired` based on the cut-off date.
 *
 * Firestore Timestamp objects (`{ _seconds, _nanoseconds }`) that survived
 * the migration are converted to ISO strings so `new Date(offer.createdAt)`
 * works in the UI.
 *
 * @param {Object|null} row - Raw Supabase row.
 * @returns {Object|null} Row with jsonb fields exposed at the top level.
 */
const FIRESTORE_TIMESTAMP_KEYS = ['createdAt', 'updatedAt', 'sentAt', 'confirmedAt', 'expiresAt'];

function normalizeFirestoreTimestamps(obj: any) {
  if (!obj || typeof obj !== 'object') return obj;
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(obj)) {
    const ts = v as any;
    if (
      v &&
      typeof v === 'object' &&
      !Array.isArray(v) &&
      typeof ts._seconds === 'number' &&
      typeof ts._nanoseconds === 'number'
    ) {
      out[k] = new Date(ts._seconds * 1000 + Math.floor(ts._nanoseconds / 1e6)).toISOString();
    } else if (v && typeof v === 'object' && !Array.isArray(v)) {
      out[k] = normalizeFirestoreTimestamps(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

export const mapOfferRow = (row: any) => {
  if (!row) return row;
  const data = normalizeFirestoreTimestamps(row.data || {});
  return {
    ...row,
    ...data,
    status: row.status ?? data.status ?? 'draft',
    createdAt: row.created_at ?? data.createdAt,
    updatedAt: row.updated_at ?? data.updatedAt,
  };
};

// The changelog data lives in its own module (see src/patchNotes.ts). Re-exported here so
// `import { PATCH_NOTES } from './constants'` keeps working with zero call-site changes.
export { PATCH_NOTES } from './patchNotes';
